import React, { useCallback } from "react";
import { ChipSelector } from "./ChipSelector";
import DayFirstDate from "./DayFirstDate";
import { ageText, isAdult, optionsFor, partTitle, questionText, stemFor } from "../lib/screenerWording";

// Attention and Activity: Quick Questionnaire (form key ADHD-Screener).
// All wording comes from the form list (config.screener). The family only answers questions here:
// no score, guide or clinician summary is ever worked out or shown on this page.

const sectionStyle = { marginBottom: 28, paddingBottom: 16, borderBottom: "1px solid #e5e7eb" };
const sectionTitle = { fontSize: "1.25rem", fontWeight: 700, color: "#1e3a5f", marginBottom: 12 };
const fieldGap = { marginBottom: 18 };
const labelStyle = { fontSize: "0.9375rem", fontWeight: 600, color: "#374151", marginBottom: 8, display: "block", lineHeight: 1.4 };
const noteStyle = { fontSize: "0.85rem", color: "#4b5563", marginTop: 6 };
const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db",
  fontSize: "0.9375rem", fontFamily: "inherit", color: "#1a202c", boxSizing: "border-box",
};
const textareaStyle = { ...inputStyle, minHeight: 90, resize: "vertical" };
const itemCard = { padding: "12px 14px", borderRadius: 10, border: "1px solid #e5e7eb", backgroundColor: "#fafafa", marginBottom: 10 };

export default function ADHDScreenerForm({ config, responses, setResponses }) {
  const s = (config && config.screener) || null;
  const r = responses || {};
  const set = useCallback((key, val) => setResponses((prev) => ({ ...prev, [key]: val })), [setResponses]);
  if (!s) return null;

  const who = r.who || "";
  const q = (id) => s.questions.find((x) => x.id === id);
  const label = (id) => questionText(q(id), who);
  const adult = isAdult(who, r.dob);
  const opts = (id) => optionsFor(s, id, who, adult);

  const field = (id) => {
    const kind = q(id).kind;
    const testId = `scr-${id}`;
    if (kind === "date") {
      const age = ageText(r[id]);
      return (
        <div style={fieldGap} key={id}>
          <label style={labelStyle}>{label(id)}</label>
          <DayFirstDate label={label(id)} notFuture inputStyle={inputStyle} value={r[id] || ""} onChange={(v) => set(id, v)} testId={testId} />
          {age ? <div style={noteStyle} data-testid="scr-age">Age: {age}</div> : null}
        </div>
      );
    }
    if (kind === "text") {
      return (
        <div style={fieldGap} key={id}>
          <label style={labelStyle} htmlFor={testId}>{label(id)}</label>
          <input id={testId} type="text" style={inputStyle} value={r[id] || ""} onChange={(e) => set(id, e.target.value)} data-testid={testId} />
        </div>
      );
    }
    if (kind === "textarea") {
      return (
        <div style={fieldGap} key={id}>
          <label style={labelStyle} htmlFor={testId}>{label(id)}</label>
          <textarea id={testId} style={textareaStyle} value={r[id] || ""} onChange={(e) => set(id, e.target.value)} data-testid={testId} />
        </div>
      );
    }
    if (kind === "yesno" || kind === "single") {
      return (
        <div style={fieldGap} key={id} data-testid={testId}>
          <ChipSelector label={label(id)} options={opts(id)} value={r[id] || ""} onChange={(v) => set(id, v)} />
        </div>
      );
    }
    if (kind === "multi") {
      const otherValue = id === "settings" ? "other" : "other";
      return (
        <div style={fieldGap} key={id} data-testid={testId}>
          <ChipSelector
            multi
            label={label(id)}
            options={opts(id)}
            value={r[id] || []}
            onChange={(v) => set(id, v)}
            revealField={id === "hopes" ? undefined : { value: otherValue, placeholder: "Please say" }}
            revealValue={r[`${id}_other`] || ""}
            onRevealChange={(t) => set(`${id}_other`, t)}
          />
        </div>
      );
    }
    return null;
  };

  const part = (n) => s.questions.filter((x) => x.part === n && x.id !== "who").map((x) => x.id);

  return (
    <div style={{ maxWidth: 800 }} data-testid="adhd-screener">
      <div style={sectionStyle}>
        <div style={fieldGap} data-testid="scr-who">
          <ChipSelector
            label={q("who").them}
            options={s.who}
            value={who}
            onChange={(v) => set("who", v)}
            revealField={{ value: "other", placeholder: "Please say (for example grandparent, partner, teacher)" }}
            revealValue={r.who_other || ""}
            onRevealChange={(t) => set("who_other", t)}
          />
        </div>
        {!who ? <p style={noteStyle}>Once you've chosen, the questions will appear below.</p> : null}
      </div>

      {who ? (
        <>
          <div style={sectionStyle} data-testid="scr-part-1">
            <h2 style={sectionTitle}>{partTitle(s.parts["1"], who)}</h2>
            {part(1).filter((id) => id !== "prev_details" || r.prev_assessed === "yes").map(field)}
          </div>

          <div style={sectionStyle} data-testid="scr-part-2">
            <h2 style={sectionTitle}>{partTitle(s.parts["2"], who)}</h2>
            {part(2).map(field)}
          </div>

          <div style={sectionStyle} data-testid="scr-part-3">
            <h2 style={sectionTitle}>{partTitle(s.parts["3"], who)}</h2>
            <p style={{ ...noteStyle, marginBottom: 12 }}>{s.snapshot_note}</p>
            <p style={{ fontSize: "1rem", fontWeight: 600, color: "#1e3a5f", marginBottom: 12 }} data-testid="scr-stem">{stemFor(s.stems, who)}</p>
            {part(3).map((id) => (
              <div key={id} style={itemCard} data-testid={`scr-item-${id}`}>
                <p style={{ fontSize: "0.9375rem", color: "#1f2937", marginBottom: 10, lineHeight: 1.45 }}>...{label(id)}</p>
                <ChipSelector options={s.rating} value={r[id] === undefined ? "" : String(r[id])} onChange={(v) => set(id, v)} />
              </div>
            ))}
          </div>

          <div style={sectionStyle} data-testid="scr-part-4">
            <h2 style={sectionTitle}>{partTitle(s.parts["4"], who)}</h2>
            {["onset", "settings"].map(field)}
            <div style={fieldGap}>
              <span style={labelStyle}>{s.impact_note}</span>
              {part(4).filter((id) => q(id).kind === "impact").map((id) => (
                <div key={id} style={itemCard} data-testid={`scr-${id}`}>
                  <p style={{ fontSize: "0.9375rem", color: "#1f2937", marginBottom: 10 }}>{label(id)}</p>
                  <ChipSelector options={s.impact_scale} value={r[id] === undefined ? "" : String(r[id])} onChange={(v) => set(id, v)} />
                </div>
              ))}
            </div>
            {["strengths", "anything_else"].map(field)}
          </div>
        </>
      ) : null}
    </div>
  );
}
